'use strict'

// Optional password rules for an application. Every rule stays off until a
// builder turns it on. Column validation calls passwordPolicyFailure wherever
// a new password is written. Expiry is checked at sign-in. Reuse compares the
// new password with the last three hashes.

const DEFAULT_LOCKOUT_MINUTES = 15
const MIN_LOCKOUT_MINUTES = 1
const MAX_LOCKOUT_MINUTES = 7 * 24 * 60
const DEFAULT_LOCKOUT_MESSAGE = 'Too many sign-in attempts. Try again later.'
const MIN_PASSWORD_LENGTH = 1
const MAX_PASSWORD_LENGTH = 256
const MIN_EXPIRY_DAYS = 1
const MAX_EXPIRY_DAYS = 3650
const REUSE_LIMIT = 3
const PASSWORD_RESET_TTL_MS = 60 * 60 * 1000
const STORED_PASSWORD_HASH = /^\$2[aby]\$\d{2}\$[./A-Za-z0-9]{53}$/

const COMMON_PASSWORDS = new Set(`
password 123456 123456789 12345678 qwerty abc123 password1 111111 123123
admin letmein welcome monkey login princess dragon passw0rd master hello
freedom whatever qazwsx trustno1 654321 jordan harley hunter ranger shadow
baseball football soccer hockey killer george andrew michelle jessica pepper
ginger summer winter sunshine iloveyou charlie donald password123 qwerty123
1q2w3e4r zaq12wsx starwars superman batman access flower mustang michael
jennifer thomas joshua ashley bailey nicole daniel matthew anthony robert
000000 121212 123qwe pass pass123 secret secret1 changeme default guest root
toor passwd test testing test123 admin123 welcome1 password2 abc12345
qwertyuiop asdfgh zxcvbn letmein1 p@ssw0rd p@ssword passw0rd1
`.trim().split(/\s+/))

function defaultPasswordPolicy () {

  return {
    minLength: null,
    blockCommon: false,
    requireNumber: false,
    requireSymbol: false,
    requireMixedCase: false,
    expiryDays: null,
    preventReuse: false,
    lockoutMinutes: DEFAULT_LOCKOUT_MINUTES,
    lockoutMessage: DEFAULT_LOCKOUT_MESSAGE,
    clearAuthenticatorOnReset: false
  }
}

function asWholeNumber (value) {

  if (typeof value === 'string' && /^\d+$/.test(value.trim())) value = Number(value.trim())
  if (typeof value !== 'number' || !Number.isInteger(value)) return null

  return value
}

function flag (value) {

  return value === true || value === 'true'
}

function optionalWhole (value, label, min, max) {

  if (value == null || value === '' || value === 0 || value === '0') return null

  const parsed = asWholeNumber(value)

  if (parsed == null) return { error: `${label} must be a whole number` }
  if (parsed < min || parsed > max) return { error: `${label} must be between ${min} and ${max}` }

  return parsed
}

function normalizePasswordPolicy (input) {

  if (input == null) return defaultPasswordPolicy()
  if (typeof input !== 'object' || Array.isArray(input)) return { error: 'Password policy must be an object' }

  const minLength = optionalWhole(input.minLength, 'Minimum length', MIN_PASSWORD_LENGTH, MAX_PASSWORD_LENGTH)

  if (minLength && minLength.error) return minLength

  const expiryDays = optionalWhole(input.expiryDays, 'Password expiry', MIN_EXPIRY_DAYS, MAX_EXPIRY_DAYS)

  if (expiryDays && expiryDays.error) return expiryDays

  const lockoutMinutes = input.lockoutMinutes == null || input.lockoutMinutes === ''
    ? DEFAULT_LOCKOUT_MINUTES
    : asWholeNumber(input.lockoutMinutes)

  if (lockoutMinutes == null) return { error: 'Lockout duration must be a whole number of minutes' }

  if (lockoutMinutes < MIN_LOCKOUT_MINUTES || lockoutMinutes > MAX_LOCKOUT_MINUTES) {

    return { error: `Lockout duration must be between ${MIN_LOCKOUT_MINUTES} and ${MAX_LOCKOUT_MINUTES} minutes` }
  }

  const message = typeof input.lockoutMessage === 'string' ? input.lockoutMessage.trim() : ''

  if (message.length > 300) return { error: 'Lockout message must be 300 characters or fewer' }

  return {
    minLength,
    blockCommon: flag(input.blockCommon),
    requireNumber: flag(input.requireNumber),
    requireSymbol: flag(input.requireSymbol),
    requireMixedCase: flag(input.requireMixedCase),
    expiryDays,
    preventReuse: flag(input.preventReuse),
    lockoutMinutes,
    lockoutMessage: message || DEFAULT_LOCKOUT_MESSAGE,
    clearAuthenticatorOnReset: flag(input.clearAuthenticatorOnReset)
  }
}

function passwordPolicyForRead (input) {

  const normalized = normalizePasswordPolicy(input)

  if (normalized.error) return defaultPasswordPolicy()

  return normalized
}

function publicPasswordPolicy (input) {

  const policy = passwordPolicyForRead(input)

  return {
    minLength: policy.minLength,
    blockCommon: policy.blockCommon,
    requireNumber: policy.requireNumber,
    requireSymbol: policy.requireSymbol,
    requireMixedCase: policy.requireMixedCase,
    expiryDays: policy.expiryDays,
    preventReuse: policy.preventReuse
  }
}

function isStoredPasswordHash (value) {

  return typeof value === 'string' && STORED_PASSWORD_HASH.test(value)
}

function isEmailAddress (value) {

  return typeof value === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
}

function passwordPolicyFailure (value, policy, extras) {

  const rules = passwordPolicyForRead(policy)

  if (value == null || value === '') return null
  if (isStoredPasswordHash(value)) return null

  const text = typeof value === 'string' ? value : String(value)

  if (rules.minLength && text.length < rules.minLength) {

    return `Use at least ${rules.minLength} characters.`
  }

  if (rules.blockCommon && COMMON_PASSWORDS.has(text.toLowerCase())) {

    return 'Choose a password that is not commonly used.'
  }

  if (rules.requireNumber && !/\d/.test(text)) return 'Include a number.'
  if (rules.requireSymbol && !/[^A-Za-z0-9]/.test(text)) return 'Include a symbol.'

  if (rules.requireMixedCase && !( /[a-z]/.test(text) && /[A-Z]/.test(text) )) {

    return 'Include both uppercase and lowercase letters.'
  }

  if (rules.preventReuse && extras && extras.reused) {

    return 'Choose a password you have not used recently.'
  }

  return null
}

function passwordExpired (changedAt, policy, now) {

  const rules = passwordPolicyForRead(policy)

  if (!rules.expiryDays || !changedAt) return false

  return now >= changedAt + rules.expiryDays * 24 * 60 * 60 * 1000
}

function passwordRuleSummary (policy) {

  const rules = passwordPolicyForRead(policy)
  const parts = []

  if (rules.minLength) parts.push(`at least ${rules.minLength} characters`)
  if (rules.blockCommon) parts.push('not a common password')
  if (rules.requireNumber) parts.push('a number')
  if (rules.requireSymbol) parts.push('a symbol')
  if (rules.requireMixedCase) parts.push('uppercase and lowercase letters')
  if (rules.expiryDays) parts.push(`a change every ${rules.expiryDays} days`)
  if (rules.preventReuse) parts.push('a password different from the last three')

  if (!parts.length) return ''

  return `Use ${parts.join(', ')}.`
}

function emailOnRecord (username, row) {

  if (isEmailAddress(username)) return String(username).trim()

  const plain = row && row.toJSON ? row.toJSON() : row

  if (!plain || typeof plain !== 'object') return ''

  for (const value of Object.values(plain)) {

    if (isEmailAddress(value)) return String(value).trim()
  }

  return ''
}

function allowedResetOrigin (value) {

  let url

  try {

    url = new URL(value)
  } catch (error) {

    return ''
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') return ''

  const host = url.hostname

  if (host === 'localhost' || host === '127.0.0.1' || host === 'app.localhost' || host.endsWith('.localhost')) {

    return url.origin
  }

  return ''
}

function passwordResetUrl (origin, application, token) {

  const base = allowedResetOrigin(origin) || 'http://localhost:10002'
  const url = new URL(base)
  const name = encodeURIComponent(String(application || '').replace(/^\/+|\/+$/g, ''))

  url.pathname = `/${name}/reset-password`
  url.search = ''
  url.hash = ''
  url.searchParams.set('token', token)

  return url.toString()
}

module.exports = {
  DEFAULT_LOCKOUT_MINUTES,
  DEFAULT_LOCKOUT_MESSAGE,
  REUSE_LIMIT,
  PASSWORD_RESET_TTL_MS,
  COMMON_PASSWORDS,
  defaultPasswordPolicy,
  normalizePasswordPolicy,
  passwordPolicyForRead,
  publicPasswordPolicy,
  isStoredPasswordHash,
  isEmailAddress,
  passwordPolicyFailure,
  passwordExpired,
  passwordRuleSummary,
  emailOnRecord,
  allowedResetOrigin,
  passwordResetUrl
}
